import {
  ClientResponse,
  ConversationEventType,
  ConversationStatus,
  LeaveConversationParams,
} from '@stripe-internal/conversations-client';
import {ApiMethods, ReportWarning} from './ApiMethods';
import {FrameworkAdapter} from './FrameworkAdapter';

type LeaveConversationHandlerDeps = {
  deleteConversation: (
    conversationId: string,
  ) => ReturnType<ApiMethods['deleteConversation']>;
  emitConversationEvent: (event: ConversationEventType) => void;
  reportWarning?: ReportWarning;
};

export class LeaveConversationHandler {
  private readonly deps: LeaveConversationHandlerDeps;

  constructor(deps: LeaveConversationHandlerDeps) {
    this.deps = deps;
  }

  call = async (params: LeaveConversationParams): Promise<ClientResponse> => {
    if (!params.conversationId) {
      return {code: 400, error: 'Missing conversationId'};
    }
    if (!params.participant?.id) {
      return {code: 400, error: 'Missing participant'};
    }

    const response = await this.deps.deleteConversation(params.conversationId);
    if (response.type === 'success' || response.code === 404) {
      this.deps.emitConversationEvent({
        name: 'conversation-typing-ended',
        conversationId: params.conversationId,
        participantId: params.participant.id,
      });
      this.deps.emitConversationEvent({
        name: 'conversation-updated',
        conversationId: params.conversationId,
        status: ConversationStatus.Inactive,
      });
      return {code: 200};
    }

    const mapped = FrameworkAdapter.toClientErrorResponse(response);
    if (mapped.code !== 401) {
      this.deps.reportWarning?.('error leaving conversation', {
        tags: {
          conversation_id: params.conversationId,
          code: response.code,
          error: response.error,
        },
      });
    }

    return mapped;
  };
}
